import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import { useDateFormatter } from './useDateFormatter';
import '../contracts/BloqueiosAgenda_SaveInput';

import type { BloqueiosAgenda_GetResponseDados } from '../contracts/BloqueiosAgenda_GetResponse';
import '../contracts/BloqueiosAgenda_CreateResponse';
import '../contracts/BloqueiosAgenda_DeleteResponse';
import '../contracts/BloqueiosAgenda_GetResponse';
import type { BloqueiosAgenda_SaveInput } from '../contracts/BloqueiosAgenda_SaveInput';
/**
 * Composable for Bloqueio Agenda (Calendar Blocking) operations
 * Provides methods to interact with the Bloqueios Agenda API
 */
export function useBloqueioAgenda() {
  const isLoading = ref(false);
  const error = ref(null);
  const { toApiDateTimeFormat, fromApiDateTimeFormat } = useDateFormatter();

  // Datetime fields that cross the API boundary for bloqueios.
  const DATETIME_FIELDS = ['bloqueio_agenda_data_hora_inicio', 'bloqueio_agenda_data_hora_fim'] as const satisfies readonly (keyof BloqueiosAgenda_GetResponseDados)[];

  /**
   * Fetch single bloqueio by ID — converts ISO 8601 UTC datetimes to Date objects
   * at the composable boundary (Typed Data Layer: API → App).
   */
  const fetchBloqueio = async (bloqueioId: number) => {
    log('useBloqueioAgenda', 'fetchBloqueio', 'Fetching bloqueio', bloqueioId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/bloqueios_agenda/get/${bloqueioId}`, { responseContract: 'BloqueiosAgenda_GetResponse' });
      log('useBloqueioAgenda', 'fetchBloqueio', 'Response', response);

      if (response.success) {
        const data = response.dados;
        if (data) {
          const record = data as Record<typeof DATETIME_FIELDS[number], Date | string | undefined>;
          for (const field of DATETIME_FIELDS) {
            const value = record[field];
            if (typeof value === 'string') {
              const parsed = fromApiDateTimeFormat(value);
              if (parsed) record[field] = parsed;
            }
          }
        }
        return data;
      } else {
        throw new Error(response.error || 'Erro ao buscar bloqueio');
      }
    } catch (err) {
      log('useBloqueioAgenda', 'fetchBloqueio', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save bloqueio (create or update) — converts datetimes to ISO 8601 UTC at the
   * composable boundary (Typed Data Layer: App → API). Accepts Date objects or
   * any string parseable by `new Date(...)` for backward compat with callers that
   * still hold local-format strings.
   */
  const saveBloqueio = async (bloqueioData: any) => {
    log('useBloqueioAgenda', 'saveBloqueio', 'Saving bloqueio', bloqueioData);
    isLoading.value = true;
    error.value = null;

    const payload: BloqueiosAgenda_SaveInput = { ...bloqueioData };
    for (const field of DATETIME_FIELDS) {
      if (payload[field]) payload[field] = toApiDateTimeFormat(payload[field]);
    }

    try {
      validate('BloqueiosAgenda_SaveInput', payload);
      // A refusal carries the backend's reason (e.g. operador fora do filtro) as an
      // `envelope-failure` error, so the modal can tell it apart from a transport failure.
      const response = await httpService.post('/index.php/bloqueios_agenda/save', payload, { responseContract: 'BloqueiosAgenda_CreateResponse', throwOnEnvelopeFailure: 'Erro ao salvar bloqueio' });
      log('useBloqueioAgenda', 'saveBloqueio', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao salvar bloqueio');
      }
    } catch (err) {
      log('useBloqueioAgenda', 'saveBloqueio', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete bloqueio
   */
  const deleteBloqueio = async (bloqueioId: number) => {
    log('useBloqueioAgenda', 'deleteBloqueio', 'Deleting bloqueio', bloqueioId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/bloqueios_agenda/delete/${bloqueioId}`, { responseContract: 'BloqueiosAgenda_DeleteResponse' });
      log('useBloqueioAgenda', 'deleteBloqueio', 'Response', response);

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao deletar bloqueio');
      }
    } catch (err) {
      log('useBloqueioAgenda', 'deleteBloqueio', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    fetchBloqueio,
    fetchBloqueioAgenda: fetchBloqueio, // Alias for compatibility
    saveBloqueio,
    saveBloqueioAgenda: saveBloqueio, // Alias for compatibility
    deleteBloqueio,
    removerBloqueioAgenda: deleteBloqueio // Alias for compatibility
  };
}
